/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import DynamicHighchartsChart from "components/charts/modern/abstract/DynamicHighchartsChart";
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import afterSetExtremes from "components/charts/modern/highcharts/afterSetExtremes";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LogLinearScaleSelector } from "components/charts/modern/inputs/AxisScaleSelector";
import ChartPeriodSelector, { ChartPeriodId } from "components/charts/modern/inputs/ChartPeriodSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import SelectedTickerSymbolsStack from "components/charts/modern/overlay/TickerSymbolSelect/SelectedTickerSymbolsStack";
import TickerSymbolSelect from "components/charts/modern/overlay/TickerSymbolSelect/TickerSymbolSelect";
import { SELECTED_TICKER_SYMBOLS_ATOM } from "components/charts/modern/overlay/TickerSymbolSelect/atoms";
import type { ChartOverlay } from "components/charts/modern/overlay/types";
import DrawdownBox from "components/charts/modern/quotes/DrawdownBox";
import ReturnBox from "components/charts/modern/quotes/ReturnBox";
import calculateMarketReturns from "components/charts/modern/quotes/calculateMarketReturns";
import type { ColorTheme } from "components/charts/modern/quotes/getColorThemeForIndex";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import QuotesChartTooltip from "components/charts/modern/tooltip/QuotesChartTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import Avatar from "components/core/Avatar";
import LocaleContext from "components/i18n/LocaleContext";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Button } from "components/ui/Button";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import { startOfYear } from "date-fns";
import addWeeks from "date-fns/addWeeks";
import compareAsc from "date-fns/compareAsc";
import isAfter from "date-fns/isAfter";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import subMonths from "date-fns/subMonths";
import subYears from "date-fns/subYears";
import type Highcharts from "highcharts/highstock";
import type {
  AlignValue,
  DashStyleValue,
  SeriesFlagsOptions,
  SeriesLineOptions,
  YAxisPlotLinesOptions,
} from "highcharts/highstock";
import { useAtomValue } from "jotai";
import QuotesHistoryQuery from "lib/graphql/queries/QuotesHistoryQuery";
import type {
  IQuotesHistoryQuery,
  IQuotesHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/QuotesHistoryQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import flatten from "lodash/flatten";
import { FoldVerticalIcon, InfoIcon } from "lucide-react";
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import { IPlatformVertical } from "types/graphqlTypes";
import { useLocalStorage } from "usehooks-ts";

interface QuotesChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function QuotesChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: QuotesChartProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const issuer = useRootIssuer();
  const theme = useTheme();
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);
  const locale = useContext(LocaleContext);
  const [axisScaleType, setAxisScaleType] = useState<AxisScaleType>("linear");

  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const crosshairColor = theme === "dark" ? colors.slate[500] : colors.slate[800];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const plotLineLabelColor = theme === "dark" ? colors.slate[400] : colors.slate[500];
  const [isPlotQuotesLimit, setIsPlotQuotesLimit] = useLocalStorage("plotQuotesLimit", false);
  const [disableTemporaryQuotesLimit, setDisableTemporaryQuotesLimit] = useState(false);
  const periods = [
    {
      period: ChartPeriodId.P1M,
      label: intl.formatMessage({
        defaultMessage: "1M",
        description: "Período do gráfico de cotação",
        id: "SYUwn6",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "1 mês",
        description: "Período do gráfico de cotação",
        id: "q9yRjj",
      }),
      from: subMonths(new Date(), 1),
    },
    {
      period: ChartPeriodId.P3M,
      label: intl.formatMessage({
        defaultMessage: "3M",
        description: "Período do gráfico de cotação",
        id: "Sbe6Sk",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "3 meses",
        description: "Período do gráfico de cotação",
        id: "/6QzwO",
      }),
      from: subMonths(new Date(), 3),
    },
    {
      period: ChartPeriodId.P6M,
      label: intl.formatMessage({
        defaultMessage: "6M",
        description: "Período do gráfico de cotação",
        id: "nnEvbr",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "6 meses",
        description: "Período do gráfico de cotação",
        id: "fs9wFD",
      }),
      from: subMonths(new Date(), 6),
    },
    {
      period: ChartPeriodId.YTD,
      label: intl.formatMessage({
        defaultMessage: "Ano",
        description: "Período do gráfico de cotação",
        id: "l7+hs7",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "Ano",
        description: "Período do gráfico de cotação",
        id: "l7+hs7",
      }),
      from: startOfYear(new Date()),
      to: new Date(),
      // Only available if 1 week away of the start of the year
      disabled: isAfter(addWeeks(startOfYear(new Date()), 1), new Date()),
    },
    {
      period: ChartPeriodId.P1Y,
      label: intl.formatMessage({
        defaultMessage: "1A",
        description: "Período do gráfico de cotação",
        id: "a6S7oJ",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "1 ano",
        description: "Período do gráfico de cotação",
        id: "J5oq/3",
      }),
      from: subYears(new Date(), 1),
    },
    {
      period: ChartPeriodId.P5Y,
      label: intl.formatMessage({
        defaultMessage: "5A",
        description: "Período do gráfico de cotação",
        id: "wlaelQ",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "5 anos",
        description: "Período do gráfico de cotação",
        id: "PNaRTF",
      }),
      from: subYears(new Date(), 5),
    },
    {
      period: ChartPeriodId.P10Y,
      label: intl.formatMessage({
        defaultMessage: "10A",
        description: "Período do gráfico de cotação",
        id: "OdRUhz",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "10 anos",
        description: "Período do gráfico de cotação",
        id: "2R8NkV",
      }),
      from: subYears(new Date(), 10),
      disabled: !selfie?.isPremium,
    },
    {
      period: ChartPeriodId.AllTime,
      label: intl.formatMessage({
        defaultMessage: "Máx.",
        description: "Periodo do gráfico de cotação",
        id: "uy31XH",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "Todo Período",
        description: "Periodo do gráfico de cotação",
        id: "3cAdbV",
      }),
      from: parseISO("1960-01-01"),
      disabled: !selfie?.isPremium || (selfie.isPremium && selfie.isTrialing),
    },
  ];
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const period = periods.find(({ period }) => {
      if (selfie?.isPremium) {
        return period === ChartPeriodId.P10Y;
      }
      return period === ChartPeriodId.P5Y;
    });
    if (!period) {
      throw new Error("Couldn't locate the default period");
    }
    return {
      period: period.period,
      startDate: period.from,
      endDate: new Date(),
    };
  });
  const selectedTickerSymbols = useAtomValue(SELECTED_TICKER_SYMBOLS_ATOM);

  const tickerSymbolsForSeries = [...tickerSymbols].concat(selectedTickerSymbols.map((node) => node.tickerSymbol));

  // Query
  const { data, previousData, loading } = useQuery<IQuotesHistoryQuery, IQuotesHistoryQueryVariables>(
    QuotesHistoryQuery,
    {
      variables: {
        tickerSymbols: [...new Set(tickerSymbolsForSeries)].slice(0, 5),
        startDate: lightFormat(selectedPeriod.startDate, "yyyy-MM-dd"),
        endDate: lightFormat(selectedPeriod.endDate, "yyyy-MM-dd"),
      },
    },
  );

  const quotesHistory = (loading && !data ? previousData?.quotesHistory : data?.quotesHistory) || [];

  const marketReturns = calculateMarketReturns(quotesHistory);

  useEffect(() => {
    if (!chartRef.current || !isPlotQuotesLimit || axisScaleType !== "logarithmic") {
      return;
    }

    chartRef.current.chart.yAxis.forEach((axis) => {
      axis.removePlotLine("min");
      axis.removePlotLine("max");
    });

    setDisableTemporaryQuotesLimit(false);
  }, [chartRef.current, isPlotQuotesLimit, axisScaleType]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chart) {
      resetZoom();
    }
  }, [quotesHistory]);

  const resetZoom = useCallback(() => {
    if (!chartRef.current) {
      return;
    }

    chartRef.current.chart.yAxis.forEach((axis) => {
      const points = flatten(axis.series.map((series) => series.points));

      const values = points.map((node) => node.y).filter(notUndefinedOrNull);

      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      const currentExtremes = axis.getExtremes();

      if (minValue === currentExtremes.min && maxValue === currentExtremes.max) {
        return;
      }

      axis.setExtremes(minValue, maxValue);
    });
  }, [chartRef.current]);

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  const series: (SeriesLineOptions | SeriesLineOptions | SeriesFlagsOptions)[] = quotesHistory.map((series, index) => {
    let selectedOverlay: ChartOverlay | null = null;

    let colorTheme: ColorTheme = getColorThemeForIndex(index, theme);

    if (quotesHistory.length === 1) {
      const [quotes] = quotesHistory;

      if (quotes.nodes.length > 0) {
        const firstQuote = quotes.nodes[0];
        const lastQuote = quotes.nodes[quotes.nodes.length - 1];

        if (firstQuote.close <= lastQuote.close) {
          colorTheme = getColorThemeForIndex(1, theme);
        } else {
          colorTheme = {
            color: theme === "dark" ? colors.red[900] : colors.red[50],
            lineColor: theme === "dark" ? colors.red[400] : colors.red[600],
          };
        }
      }
    }

    const options: SeriesLineOptions = {
      showInNavigator: index === 0,
      id: series.tickerSymbol,
      type: "line",
      animation: false,
      name: series.tickerSymbol,
      color: colorTheme.lineColor,
      lineWidth: 2,
      data: series.nodes
        .map((node) => [parseISO(node.date).getTime(), node.close])
        .sort((a, b) => compareAsc(a[0], b[0])),
      custom: {
        tickerSymbol: series.tickerSymbol,
        primaryColor: colorTheme.color,
        secondaryColor: colorTheme.lineColor,
        overlay: selectedOverlay,
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: colorTheme.color,
        color: colorTheme.color,
        lineColor: colorTheme.lineColor,
        symbol: "circle",
      },
    };
    return options;
  });

  const minValues = quotesHistory.map((series) => Math.min(...series.nodes.map((node) => node.close)));
  const maxValues = quotesHistory.map((series) => Math.max(...series.nodes.map((node) => node.close)));
  const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;
  const maxValue = maxValues.length > 0 ? Math.min(...maxValues) : 0;

  const currentQuote =
    quotesHistory.length > 0 && quotesHistory[0].nodes.length > 0
      ? quotesHistory[0].nodes[quotesHistory[0].nodes.length - 1].close
      : 0;

  const lastPriceNode =
    quotesHistory.length > 0 && quotesHistory[0].nodes.length > 0
      ? quotesHistory[0].nodes[quotesHistory[0].nodes.length - 1]
      : null;
  const previousPriceNode =
    quotesHistory.length > 0 && quotesHistory[0].nodes.length >= 2
      ? quotesHistory[0].nodes[quotesHistory[0].nodes.length - 2]
      : null;
  const isPriceUp = lastPriceNode && previousPriceNode ? lastPriceNode.close >= previousPriceNode.close : null;
  const priceChangePercent =
    lastPriceNode && previousPriceNode && previousPriceNode.close !== 0
      ? ((lastPriceNode.close - previousPriceNode.close) / previousPriceNode.close) * 100
      : null;

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
      events: {
        render() {
          const chart = this as Highcharts.Chart;

          const plotLines: YAxisPlotLinesOptions[] | undefined =
            isPlotQuotesLimit && !disableTemporaryQuotesLimit && quotesHistory.length > 0
              ? flatten(
                  quotesHistory.map((node, index) => {
                    const series = chart.series.find((serie) => serie.name === node.tickerSymbol);

                    if (!series) {
                      return null;
                    }

                    let colorTheme: ColorTheme = getColorThemeForIndex(index, theme);

                    const firstQuote = series.points[0].y;
                    const lastQuote = series.points[series.points.length - 1].y;

                    if (firstQuote && lastQuote && firstQuote >= lastQuote) {
                      colorTheme = {
                        color: theme === "dark" ? colors.red[900] : colors.red[50],
                        lineColor: theme === "dark" ? colors.red[400] : colors.red[600],
                      };
                    }

                    if (chart.yAxis.length > 0) {
                      chart.yAxis[0].removePlotLine(`min`);
                      chart.yAxis[0].removePlotLine(`max`);
                    }

                    const minValue = Math.min(...series.points.map((node) => node.y).filter(notUndefinedOrNull));
                    const maxValue = Math.max(...series.points.map((node) => node.y).filter(notUndefinedOrNull));

                    return [
                      {
                        id: `min`,
                        color: colorTheme.lineColor,
                        value: minValue,
                        dashStyle: "Dash" as DashStyleValue,
                        width: 1,
                        zIndex: 4,
                        label: {
                          enabled: true,
                          align: "center" as AlignValue,
                          text: `Mín: ${minValue}`,
                          style: {
                            color: plotLineLabelColor,
                            fontFamily: aeonik.style.fontFamily,
                            fontWeight: "500",
                            fontSize: "12px",
                          },
                        },
                      },
                      {
                        id: `max`,
                        color: colorTheme.lineColor,
                        value: maxValue,
                        dashStyle: "Dash" as DashStyleValue,
                        width: 1,
                        zIndex: 4,
                        label: {
                          enabled: true,
                          align: "center" as AlignValue,
                          text: `Máx: ${maxValue}`,
                          style: {
                            color: plotLineLabelColor,
                            fontFamily: aeonik.style.fontFamily,
                            fontWeight: "500",
                            fontSize: "12px",
                          },
                        },
                      },
                    ];
                  }),
                ).filter(notUndefinedOrNull)
              : undefined;

          if (plotLines && chart.yAxis.length > 0) {
            plotLines.forEach((node) => {
              chart.yAxis[0].addPlotLine(node);
            });
          }
        },
      },
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
      events: {
        afterSetExtremes(this, event) {
          afterSetExtremes(this, event);
        },
      },
    },
    yAxis: {
      min: minValue,
      max: maxValue,
      type: axisScaleType,
      scrollbar: {
        enabled: false,
      },
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        formatter() {
          // Verifica se o rótulo atual é o valor que você deseja destacar
          const purpleColor = theme === "dark" ? colors.purple[400] : colors.purple[500];
          if (this.value === currentQuote) {
            return `<span style="color:${purpleColor};font-weight:bold;">${this.value}</span>`;
          }
          return String(this.value);
        },
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: false,
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...(theme === "dark" ? darkNavigatorStyles : navigatorStyles),
      yAxis: {
        ...(theme === "dark" ? darkNavigatorStyles : navigatorStyles).yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        min: minValue,
        max: maxValue,
        type: axisScaleType,
        zoomEnabled: false,
      },
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter() {
        return renderToStaticMarkup(<QuotesChartTooltip locale={locale} context={this} intl={intl} />);
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        dataGrouping: {
          enabled: true,
          units: [["week", [1]]],
          approximation: (groupData: number[]) => {
            return groupData[groupData.length - 1];
          },
        },
        states: {
          hover: {
            lineWidth: 2,
          },
        },
      },
      flags: {
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
      line: {
        color: colors.purple[600],
        lineWidth: 2,
        marker: {
          enabled: false,
          fillColor: colors.emerald[50],
          lineColor: colors.emerald[600],
          lineWidth: 2,
          width: 8,
          height: 8,
        },
      },
    },
  };

  const hasZeroValue = quotesHistory
    .map((node) => node.nodes.some((node) => node.close === 0))
    .some((node) => node === true);

  const tradingCurrency = issuer.__typename === "AmericanCompanyIssuer" ? "USD" : "BRL";
  const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: tradingCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    minimumSignificantDigits: 2,
  });

  return (
    <div className="flex h-full w-full flex-col space-y-4">
      <div className="relative flex flex-col items-center justify-between sm:flex-row">
        <div className="grid w-full grid-cols-3 items-center gap-4 md:flex">
          {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
            <div className="col-span-3">
              <TickerSymbolSelector
                type="multiple"
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            </div>
          )}

          <ChartPeriodSelector
            periods={periods}
            value={selectedPeriod.period}
            onChange={(period, startDate, endDate) => {
              if (chartRef.current) {
                chartRef.current.chart.xAxis.forEach((axis) => {
                  axis.setExtremes();
                });
              }

              setSelectedPeriod({
                period,
                startDate,
                endDate,
              });
            }}
          />

          <div className="col-span-2 flex gap-4 md:contents">
            {!hasZeroValue && <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />}

            <button
              type="button"
              disabled={quotesHistory.length > 1 || axisScaleType === "logarithmic"}
              className={classNames(
                "rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600",
                "relative dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                {
                  "bg-violet-50 text-violet-600 dark:bg-violet-600/20 dark:text-violet-200":
                    isPlotQuotesLimit && !disableTemporaryQuotesLimit,
                },
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "disabled:cursor-not-allowed",
              )}
              onClick={() => {
                if (isPlotQuotesLimit && chartRef.current) {
                  chartRef.current.chart.yAxis.forEach((axis) => {
                    axis.removePlotLine("min");
                    axis.removePlotLine("max");
                  });
                }

                setIsPlotQuotesLimit((previous) => !previous);
              }}
            >
              <FoldVerticalIcon />
            </button>
          </div>

          <TickerSymbolSelect
            excludeTickerSymbols={tickerSymbols}
            issuerTypes={(() => {
              if (issuer.__typename === "AmericanCompanyIssuer") {
                return [IPlatformVertical.Stocks];
              }
              if (issuer.__typename === "BrazilianCompanyIssuer") {
                return [IPlatformVertical.Acoes];
              }
              if (issuer.__typename === "BrazilianRealEstateFund") {
                return [IPlatformVertical.Fiis];
              }
              return [];
            })()}
          >
            <Button
              variant="secondary"
              className="col-span-3 row-start-1 w-full disabled:pointer-events-auto disabled:cursor-not-allowed sm:max-w-xs lg:w-auto"
              disabled={!(selfie && selfie.isPremium)}
            >
              Compare
            </Button>
          </TickerSymbolSelect>
        </div>

        <div className="hidden items-center space-x-3 sm:inline-flex">
          {lastPriceNode && (
            <div className="flex flex-col items-end text-right">
              <span
                className={classNames("font-aeonik text-base font-bold tabular-nums leading-tight tracking-tight", {
                  "text-emerald-600 dark:text-emerald-400": isPriceUp === true,
                  "text-red-500 dark:text-red-400": isPriceUp === false,
                  "text-slate-800 dark:text-slate-200": isPriceUp === null,
                })}
              >
                {moneyFormatter.format(lastPriceNode.close)}
              </span>
              <div className="mt-0.5 flex items-center gap-1.5">
                {priceChangePercent !== null && (
                  <span
                    className={classNames("text-[11px] font-semibold tabular-nums leading-none", {
                      "text-emerald-500 dark:text-emerald-400": isPriceUp === true,
                      "text-red-500 dark:text-red-400": isPriceUp === false,
                    })}
                  >
                    {isPriceUp ? "+" : ""}
                    {intl.formatNumber(priceChangePercent, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    %
                  </span>
                )}
                <span className="text-[11px] leading-none text-slate-400 dark:text-slate-400">
                  {intl.formatDate(parseISO(lastPriceNode.date), {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          )}
          <SimpleTextTooltip content={issuer.name}>
            <Avatar
              src={issuer.__typename !== "BrazilianRealEstateFund" && issuer.niceLogo ? issuer.niceLogo.url : null}
              hashValue={issuer._id}
              alt={issuer.name}
              size={40}
              squircle={true}
            />
          </SimpleTextTooltip>

          <SelectedTickerSymbolsStack isLoading={loading} />
        </div>
      </div>

      <DynamicHighchartsChart ref={chartRef} options={options} className="h-96 min-h-96 md:h-full md:max-h-[466px]" />

      <div className="grid grid-cols-2 gap-4 md:flex md:flex-row">
        {/* Return boxes */}
        {marketReturns
          .filter((node) => {
            return tickerSymbols.includes(node.tickerSymbol);
          })
          .map((node, index) => {
            const colorTheme = getColorThemeForIndex(index, theme);
            return (
              <Fragment key={node.tickerSymbol}>
                <ReturnBox
                  title={intl.formatMessage({ defaultMessage: "% Total", id: "vccktX" })}
                  tickerSymbol={node.tickerSymbol}
                  firstPoint={node.firstPoint}
                  lastPoint={node.lastPoint}
                  returnRate={node.totalReturn}
                  textColor={colorTheme.lineColor}
                  className="bg-slate-50 dark:bg-slate-800"
                  tickerSymbolClassName="bg-slate-400/10 text-slate-800 dark:bg-slate-700/50 dark:text-slate-200"
                />

                {node.annualReturn && (
                  <ReturnBox
                    title={intl.formatMessage({ defaultMessage: "Retorno Anual", id: "Z8+BV4" })}
                    tickerSymbol={node.tickerSymbol}
                    firstPoint={node.firstPoint}
                    lastPoint={node.lastPoint}
                    returnRate={node.annualReturn}
                    textColor={colorTheme.lineColor}
                    className="bg-sky-50 dark:bg-sky-900/20"
                    tickerSymbolClassName="bg-sky-400/10 text-sky-800 dark:bg-sky-700/50 dark:text-sky-200"
                  />
                )}
              </Fragment>
            );
          })}

        {/* Drawdown boxes */}
        {(issuer.__typename === "BrazilianCompanyIssuer" || issuer.__typename === "AmericanCompanyIssuer") &&
          selfie?.isPremium &&
          issuer.securities
            .filter((security) => {
              return (
                tickerSymbolsForSeries.includes(security.tickerSymbol) &&
                security.trailingReturns != null &&
                security.trailingReturns.prices?.allTimeHigh != null
              );
            })
            .map((security) => {
              if (
                security.trailingReturns == null ||
                security.trailingReturns.prices?.allTimeHigh == null ||
                security.trailingReturns.drawdownFromAllTimeHigh == null
              ) {
                return null;
              }
              return (
                <DrawdownBox
                  title={intl.formatMessage({ defaultMessage: "Retorno Topo", id: "d/U9o4" })}
                  tickerSymbol={security.tickerSymbol}
                  tradingCurrency={tradingCurrency}
                  allTimeHigh={security.trailingReturns.prices?.allTimeHigh}
                  drawdownFromAllTimeHigh={security.trailingReturns.drawdownFromAllTimeHigh}
                />
              );
            })}

        {issuer.__typename === "BrazilianRealEstateFund" &&
          issuer.trailingReturns &&
          issuer.trailingReturns.prices?.allTimeHigh &&
          issuer.trailingReturns.drawdownFromAllTimeHigh &&
          selfie?.isPremium && (
            <DrawdownBox
              title={intl.formatMessage({ defaultMessage: "Retorno Topo", id: "d/U9o4" })}
              tickerSymbol={issuer.tickerSymbol}
              tradingCurrency="BRL"
              allTimeHigh={issuer.trailingReturns.prices?.allTimeHigh}
              drawdownFromAllTimeHigh={issuer.trailingReturns.drawdownFromAllTimeHigh}
            />
          )}
      </div>

      <div className="flex items-start gap-2.5 rounded-xl bg-slate-50 p-3.5 dark:bg-slate-800/50">
        <InfoIcon className="mt-0.5 size-4 shrink-0 text-slate-500 dark:text-slate-400" strokeWidth={2} />
        <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          <FormattedMessage
            defaultMessage="O retorno do gráfico de cotação do Fundamentei considera todos os eventos passados, como: dividendos, desdobramentos, bonificação e subscrição."
            description="Aviso sobre como o retorno do gráfico de cotação é calculado"
            id="TJq4DA"
          />
        </p>
      </div>
    </div>
  );
}
